import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Reveal from '../components/ui/Reveal';
import API, { UPLOADS_URL } from '../api/axios';

const resolveImage = (src) => {
  if (!src) return '';
  if (src.startsWith('/frontend/')) return src.replace('/frontend/', '/');
  if (src.startsWith('http') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const paras = (text) => (text || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

const DEFAULT_SECTIONS = [
  { section_key: 'hero', section_type: 'hero', image: '/images/page-banners/other-facilities.jpg' },
  {
    section_key: 'intro',
    section_type: 'intro',
    kicker: 'Other Facilities',
    title: 'A Modern Learning Environment Beyond the Classroom',
    image: '/images/facilities/Infrastructure.jpg',
    description:
      'At St. Andrews World School, Indirapuram, we believe that education should provide children with opportunities to learn, explore, innovate and grow in every aspect of life.\n\nAs a leading school in Indirapuram, our campus offers thoughtfully planned facilities that support academics, sports, technology, creativity, health and student safety.\n\nOur focus is to create a balanced and future-ready environment for families searching for the best school in Indirapuram, where children receive much more than classroom education.',
  },
  {
    section_key: 'sports_physical_development',
    section_type: 'card',
    icon: 'fa-futbol',
    image: '/images/coactivities/Football.jpg',
    title: 'Sports & Physical Development',
    description: 'Sports play an important role in developing a child\u2019s physical fitness, confidence and personality. Students participate in activities that build strength, fitness, teamwork, leadership, discipline, coordination, confidence and sportsmanship.',
  },
  {
    section_key: 'robotics_ai_future_ready',
    section_type: 'card',
    icon: 'fa-microchip',
    image: '/images/facilities/Computer-Lab.jpg',
    title: 'Robotics, Artificial Intelligence & Future-Ready Learning',
    description: 'Students are introduced to AI, Robotics, Coding and STEM-based learning through engaging and practical activities \u2014 building logical thinking, computational thinking, creativity, problem-solving and coding skills to become creators and problem-solvers.',
  },
  {
    section_key: 'smart_classrooms',
    section_type: 'card',
    icon: 'fa-chalkboard-user',
    image: '/images/facilities/Junior-Classroom.jpg',
    title: 'Smart & Technology-Enabled Classrooms',
    description: 'Technology-enabled learning makes classroom sessions more interactive, visual and engaging. Digital resources help students understand concepts better while encouraging participation, curiosity and independent thinking.',
  },
  {
    section_key: 'school_transport',
    section_type: 'card',
    icon: 'fa-bus',
    image: '/images/coactivities/curriculum4.jpg',
    title: 'Safe & Reliable School Transport',
    description: 'Our transport facility provides students with an organised, safe and comfortable journey between home and school. Responsible supervision, structured procedures and safety-focused practices ensure a smooth travel experience.',
  },
  {
    section_key: 'medical_first_aid',
    section_type: 'card',
    icon: 'fa-kit-medical',
    image: '/images/facilities/Infrastructure.jpg',
    title: 'Medical & First-Aid Support',
    description: 'The health and well-being of every student are important to us. Medical and first-aid support attends to minor injuries, sudden discomfort or health concerns during school hours, with timely assistance and parent notification.',
  },
  {
    section_key: 'campus_security',
    section_type: 'card',
    icon: 'fa-shield-halved',
    image: '/images/coactivities/curriculum3.jpg',
    title: 'Campus Security & Student Safety',
    description: 'A secure learning environment allows children to explore and participate with confidence. Controlled access, responsible monitoring and staff awareness maintain a safe, disciplined and supportive campus.',
  },
  {
    section_key: 'science_experiential_learning',
    section_type: 'card',
    icon: 'fa-flask',
    image: '/images/facilities/Science-Lab.jpg',
    title: 'Science & Experiential Learning',
    description: 'Experimentation, observation and hands-on activities connect classroom concepts with real-world applications, building curiosity, analytical thinking, scientific temperament and problem-solving ability.',
  },
  {
    section_key: 'library_reading',
    section_type: 'card',
    icon: 'fa-book-open',
    image: '/images/facilities/Library.jpg',
    title: 'Library & Reading Culture',
    description: 'Reading builds vocabulary, communication skills, imagination and knowledge. Students are encouraged to develop regular reading habits and explore age-appropriate books and learning resources.',
  },
  {
    section_key: 'creative_activity_learning',
    section_type: 'card',
    icon: 'fa-palette',
    image: '/images/coactivities/curriculum-1.jpg',
    title: 'Creative & Activity-Based Learning',
    description: 'Art, music, dance and activity-based learning let students express themselves, explore new interests and develop confidence beyond academics \u2014 contributing to overall personality development.',
  },
  {
    section_key: 'cta',
    section_type: 'cta',
    icon: 'fa-building-columns',
    title: 'Learn Today. Innovate Tomorrow. Grow with Confidence.',
    description: 'Every facility at St. Andrews World School, Indirapuram is designed to contribute to a child\u2019s development. Discover the complete learning experience for yourself.',
    button_text: 'Enquire for Admission',
    button_url: '/admission',
    button2_text: 'View Gallery',
    button2_url: '/gallery',
  },
];

const OtherFacilitiesPage = () => {
  const title = 'Other Facilities';
  const [facilities, setFacilities] = useState([]);
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const [facRes, secRes, pageRes] = await Promise.allSettled([
          API.get('/facilities/public'),
          API.get('/other-facilities/sections'),
          API.get('/pages/public/other-facilities')
        ]);
        if (!mounted) return;
        if (facRes.status === 'fulfilled' && facRes.value.data?.data?.length) {
          setFacilities(facRes.value.data.data);
        }
        if (secRes.status === 'fulfilled' && secRes.value.data?.data?.length) {
          setSections(secRes.value.data.data);
        }
        if (pageRes.status === 'fulfilled' && pageRes.value.data?.data) {
          setPageData(pageRes.value.data.data);
        }
      } catch (err) {
        /* ignore */
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, []);

  const heroSec = sections.find((s) => s.section_type === 'hero');
  const bannerImg = heroSec?.image
    ? resolveImage(heroSec.image)
    : (pageData?.banner_image
        ? resolveImage(pageData.banner_image)
        : '/images/page-banners/other-facilities.jpg');

  const displayCards = facilities.length > 0
    ? facilities.map((f) => ({
        section_key: f.slug,
        title: f.title,
        description: f.description ? f.description.replace(/<[^>]+>/g, '').trim() : '',
        icon: f.icon || 'fa-building-columns',
        image: f.image,
      }))
    : DEFAULT_SECTIONS.filter((s) => s.section_type === 'card');

  const intro = sections.find((s) => s.section_type === 'intro') || DEFAULT_SECTIONS.find((s) => s.section_type === 'intro');
  const introParas = paras(intro && intro.description);

  return (
    <>
      <SEO
        slug="other-facilities"
        title={title}
        description="Other Facilities at St. Andrews World School, Indirapuram."
        keywords="Other Facilities, transport, medical, SAWS Indirapuram"
      />

      <section className="page-hero">
        <img
          src={bannerImg}
          alt={title}
          className="page-hero-img"
          loading="eager"
          onError={(e) => {
            if (!e.target.dataset.tried) {
              e.target.dataset.tried = 1;
              e.target.src = '/images/page-banners/other-facilities.jpg';
            }
          }}
        />
        <div className="page-hero-content">
          <div className="container">
            <div className="page-hero-inner" />
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-16 md:py-20 bg-white relative overflow-hidden">
        <div className="absolute top-24 -right-24 w-96 h-96 rounded-full bg-[#1B3160]/5 blur-3xl" aria-hidden />
        <div className="absolute bottom-0 -left-32 w-80 h-80 rounded-full bg-[#D4A73E]/10 blur-3xl" aria-hidden />
        <div className="container relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 md:gap-16 items-center">
            <Reveal className="relative max-lg:order-2">
              <div className="absolute -inset-1 bg-gradient-to-br from-[#1B3160]/30 to-[#D4A73E]/20 rounded-3xl blur-lg opacity-60" aria-hidden />
              <img
                src={resolveImage(intro && intro.image)}
                alt={(intro && intro.title) || 'Campus at St. Andrews World School, Indirapuram'}
                className="relative w-full aspect-[4/3] object-cover rounded-2xl shadow-2xl"
                loading="lazy"
              />
            </Reveal>
            <div className="max-lg:order-1">
              <Reveal>
                <div className="kicker">{intro && (intro.kicker || 'Other Facilities')}</div>
                <h2 className="text-[30px] md:text-[38px] font-bold text-primary mt-2 leading-tight">
                  {(intro && intro.title) || 'A Modern Learning Environment Beyond the Classroom'}
                </h2>
                <div className="w-14 h-[3px] bg-[#D4A73E] rounded-full my-6"></div>
              </Reveal>
              <div className="space-y-5">
                {introParas.map((para, index) => (
                  <Reveal key={index} delayMs={100 + index * 60}>
                    <p className="text-body text-[16px] md:text-[17px] leading-relaxed">{para}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FACILITIES GRID */}
      <section className="py-16 md:py-20 bg-light">
        <div className="container">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <div className="kicker">What We Offer</div>
              <h2 className="text-[26px] md:text-[32px] font-bold text-primary mt-2 leading-tight">
                Facilities That Support Every Learner
              </h2>
              <div className="w-14 h-[3px] bg-[#D4A73E] rounded-full mx-auto mt-4"></div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {displayCards.map((item, index) => (
              <Reveal key={item.section_key || item.title || index} delayMs={index * 60}>
                <article className="group bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-full flex flex-col">
                  <div className="relative overflow-hidden">
                    <img
                      src={resolveImage(item.image)}
                      alt={`${item.title} at St. Andrews World School, Indirapuram`}
                      className="w-full h-[280px] object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6 flex-1">
                    <div className="flex items-center gap-3">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-all duration-300">
                        <i className={`fas ${item.icon}`} aria-hidden />
                      </div>
                      <h3 className="text-primary font-bold text-lg leading-snug">{item.title}</h3>
                    </div>
                    <p className="text-body text-[14px] leading-relaxed mt-3">{item.description}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default OtherFacilitiesPage;